/**
 * useSessionExpiryStore — single owner of the server-session substrate
 * plus a sibling draft-TTL tier.
 *
 * Server tier (`speedtest-session` + `speedtest-session-expires`, 2 h):
 *   Owns the token AND the expiry timestamp atomically. The api store
 *   calls `setServerSession(token, expiresAt)` on every `createSession`
 *   resolve; `clearServerSession()` clears both refs and both
 *   localStorage keys at once. Token + expiry live together so any
 *   boot sweep that decides the session is stale invalidates both
 *   halves in one move.
 *
 * Draft tier (`survey-*`, 24 h sliding):
 *   The user's in-progress survey form. Owned end-to-end by the client.
 *   `touchDraft()` slides the expiry forward on every edit so the draft
 *   survives long inactivity windows and arbitrary numbers of test
 *   sessions in a single user-day.
 *
 * The two tiers compose: an expired server session does NOT clear a fresh
 * draft (and vice-versa). `clearExpiredOnly()` is the public revalidation
 * entry point; it touches only the tier(s) currently expired.
 */

import { useTimer } from "@mkbabb/glass-ui/reactive";
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type { Ref, ComputedRef } from "vue";

import { typedStorage } from "@src/utils/typedStorage.ts";

/** Server-issued session expiry timestamp (ms epoch). 2 h hard TTL. */
export const SERVER_SESSION_EXPIRY_KEY = "speedtest-session-expires";
/** Server-issued session token. */
export const SESSION_KEY = "speedtest-session";
/** Client-derived survey draft expiry timestamp (ms epoch). 24 h sliding from last edit. */
export const DRAFT_EXPIRY_KEY = "speedtest-draft-expires";

/** 24 hours, sliding from the last draft edit. */
export const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

const MAX_TIMER_DELAY_MS = 2_147_483_647;

/**
 * Read the boot-time session token raw from localStorage, shape-guarded.
 *
 * The token is stored unencoded (historical wire format) so we cannot
 * route it through `typedStorage.get`'s JSON parse. The shape-guard
 * rejects payloads that fail the type predicate — the empty string and
 * the literal strings `"undefined"` / `"null"` are the corruption shapes
 * a pre-`b08dd6ce` boot can leave behind. Corrupt payloads are removed
 * so the next boot sees a clean substrate.
 */
function readBootSessionToken(): string | null {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw === null) return null;
    if (raw.length === 0 || raw === "undefined" || raw === "null") {
        try {
            localStorage.removeItem(SESSION_KEY);
        } catch {
            // Storage quota / disabled — leave it; the predicate still
            // refuses the corrupt payload at the in-memory tier.
        }
        return null;
    }
    return raw;
}

export const useSessionExpiryStore = defineStore("sessionExpiry", () => {
    const serverSessionExpiresAt: Ref<number | null> = ref(
        typedStorage.get<number | null>(SERVER_SESSION_EXPIRY_KEY, null),
    );
    // The session token is stored as a raw string under SESSION_KEY (not
    // JSON-encoded — historical wire format every consumer already speaks).
    // Read raw on construction so a pre-existing session bound by an older
    // page lifetime survives the boot sweep.
    //
    // AJ-W1-ζ Path ζ-A: shape-guard the raw read symmetrically with the
    // `setServerSession` defensive contract. A pre-`b08dd6ce` boot that
    // wrote the literal string `"undefined"` (or `"null"`) into the
    // SESSION_KEY persists across reloads — the api store would happily
    // send it as `X-Session-Token` to the backend, which would reject
    // every request. Reject the corrupt payload at the read boundary and
    // clear the localStorage substrate so the next boot sees a clean
    // surface, matching the contract `setServerSession` already enforces
    // at the write boundary.
    const sessionToken: Ref<string | null> = ref(readBootSessionToken());

    const draftExpiresAt: Ref<number | null> = ref(
        typedStorage.get<number | null>(DRAFT_EXPIRY_KEY, null),
    );

    const isServerSessionExpired: ComputedRef<boolean> = computed(() => {
        const exp = serverSessionExpiresAt.value;
        return exp !== null && Date.now() >= exp;
    });

    const isDraftExpired: ComputedRef<boolean> = computed(() => {
        const exp = draftExpiresAt.value;
        return exp !== null && Date.now() >= exp;
    });

    const serverCleanupTimer = useTimer(() => clearServerSession(), 0, {
        immediate: false,
    });
    const draftCleanupTimer = useTimer(() => clearDraft(), 0, {
        immediate: false,
    });

    /** Remove server-tier keys + in-memory token. Leaves draft + survey-* untouched. */
    function clearServerSession(): void {
        serverCleanupTimer.stop();
        typedStorage.remove(SESSION_KEY);
        typedStorage.remove(SERVER_SESSION_EXPIRY_KEY);
        serverSessionExpiresAt.value = null;
        sessionToken.value = null;
    }

    /** Remove draft-tier keys: draft expiry, all survey-* blobs, all draftId keys. */
    function clearDraft(): void {
        draftCleanupTimer.stop();
        typedStorage.remove(DRAFT_EXPIRY_KEY);
        typedStorage.clearPrefix("survey-");
        draftExpiresAt.value = null;
    }

    /** Idempotent revalidation: clears whichever tier(s) have expired. */
    function clearExpiredOnly(): void {
        if (isServerSessionExpired.value) clearServerSession();
        if (isDraftExpired.value) clearDraft();
    }

    /**
     * Single transactional write of the server-session substrate.
     *
     * Stores token + expiry to in-memory refs AND to localStorage in one
     * atomic action, then schedules the auto-cleanup timer for the new
     * expiry. The api store calls this on `createSession` resolve so the
     * token and expiry stay in lockstep across every boot.
     *
     * Defensive contract (AD.W1.T7): the api store destructures
     * `{sessionId, sessionActiveUntil}` off the createSession response —
     * if a future server-shape regression hands me `undefined` (or any
     * non-string), I refuse the write and warn rather than serialise
     * the literal string `"undefined"` into localStorage. The previous
     * `08aa01f2` shape-mismatch corrupted the substrate exactly this way;
     * the guard guarantees the in-memory + persisted token always satisfy
     * the wire contract.
     */
    function setServerSession(token: string, expiresAt: Date): void {
        if (typeof token !== "string" || token.length === 0) {
            console.warn(
                "[useSessionExpiryStore] setServerSession refused non-string token",
                { tokenType: typeof token, expiresAt },
            );
            return;
        }
        const ts = expiresAt.getTime();
        sessionToken.value = token;
        serverSessionExpiresAt.value = ts;
        if (typeof localStorage !== "undefined") {
            localStorage.setItem(SESSION_KEY, token);
        }
        typedStorage.set(SERVER_SESSION_EXPIRY_KEY, ts);
        scheduleServerCleanup(ts);
    }

    /** Slide draft expiry forward by the full draft TTL. Call on every edit. */
    function touchDraft(): void {
        const ts = Date.now() + DRAFT_TTL_MS;
        draftExpiresAt.value = ts;
        typedStorage.set(DRAFT_EXPIRY_KEY, ts);
        scheduleDraftCleanup(ts);
    }

    function scheduleServerCleanup(ts: number): void {
        serverCleanupTimer.stop();
        const remaining = ts - Date.now();
        if (remaining <= 0) {
            clearServerSession();
            return;
        }
        serverCleanupTimer.start(Math.min(remaining, MAX_TIMER_DELAY_MS));
    }

    function scheduleDraftCleanup(ts: number): void {
        draftCleanupTimer.stop();
        const remaining = ts - Date.now();
        if (remaining <= 0) {
            clearDraft();
            return;
        }
        draftCleanupTimer.start(Math.min(remaining, MAX_TIMER_DELAY_MS));
    }

    // On creation: expired tiers clear; future tiers schedule cleanup.
    if (isServerSessionExpired.value) {
        clearServerSession();
    } else if (serverSessionExpiresAt.value !== null) {
        scheduleServerCleanup(serverSessionExpiresAt.value);
    }

    if (isDraftExpired.value) {
        clearDraft();
    } else if (draftExpiresAt.value !== null) {
        scheduleDraftCleanup(draftExpiresAt.value);
    }

    return {
        // Server tier
        sessionToken,
        serverSessionExpiresAt,
        isServerSessionExpired,
        setServerSession,
        clearServerSession,
        // Draft tier
        draftExpiresAt,
        isDraftExpired,
        touchDraft,
        clearDraft,
        // Composite
        clearExpiredOnly,
    };
});
